import React, { useEffect, useState, memo } from "react";
import styled from "styled-components";
import Swal from 'sweetalert2';

const UpdateLegend = ({ selectedBlock, onClose }) => {
    const [user_id, setUserId] = useState(0);
    const [options, setOptions] = useState([]);
    const [userOption, setUserOption] = useState('')
    const [waiting, setWaiting] = useState(false)
    
    const serverHost = process.env.REACT_APP_SERVER_HOST
    
    const fetchOptions = async () => {
        try {
            const response = await fetch(`${serverHost}/legend/options?block=${selectedBlock}`);
            const data = await response.json();
            setOptions(data.options);
        } catch (error) {
            console.log('Error fetching options:', error);
        }
    };

    const handleTextareaKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            submitOption(event);
        }
    };

    const submitOption = async (event) => {
        event.preventDefault();
        setWaiting(true);
        
        if (userOption.trim().length < 3 || userOption.trim().length > 20) {
            Swal.fire({
                title: 'Failed to submit',
                text: 'The name should have a length between 3 to 20',
                icon: 'error',
                confirmButtonText: 'Close'
            });
            setWaiting(false);
            return;
        }

        const formData = new FormData();

        formData.append('created_for', selectedBlock);
        formData.append('user_id', user_id);
        formData.append('option_name', userOption.trim());

        try {
            const response = await fetch(`${serverHost}/legend`, {
                method: 'POST',
                body: formData,
            });   

            const result = await response.json();
        
            if (response.status === 200) {
                setUserOption('');
                setOptions(result.options);
            } else {
                Swal.fire({
                    title: 'Failed to submit',
                    text: result.error,
                    icon: 'error',
                    confirmButtonText: 'Close'
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Failed to submit',
                text: error,
                icon: 'error',
                confirmButtonText: 'Close'
            });
        }
        setWaiting(false);
    };

    const voteOption = async (option_id) => {
        const formData = new FormData();
        
        formData.append('option_id', option_id)
        formData.append('user_id', user_id)
        formData.append('created_for', selectedBlock)
    
        try {
            const response = await fetch(`${serverHost}/legend/vote`, {
                method: 'POST',
                body: formData,
            });      
            const result = await response.json();
    
            if (response.status === 200) {
                setOptions(result.options);
            } else {
                Swal.fire({
                    title: 'Failed to vote',
                    text: result.error,
                    icon: 'error',
                    confirmButtonText: 'Close'
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Failed to vote',
                text: error,
                icon: 'error',
                confirmButtonText: 'Close'
            });
        }
    }

    const Option = memo(({ optionData }) => (
        <OptionContainer>
            <span>{optionData.option_name}</span>
            <span>{optionData.liked_by.length} <i className="fa fa-star"
                style={{ color: optionData.liked_by.includes(user_id) ? "gold" : "lightgrey" }}
                onClick={() => voteOption(optionData.option_id)}/>
            </span>
        </OptionContainer>
    ));

    useEffect(() => {
        setUserId(parseInt(sessionStorage.getItem("user_id"), 10) || 0);
        fetchOptions();
    }, [selectedBlock]);

    return (
        <>
            <Overlay />
            <LegendDialogue>
                <Title><i className="fa fa-pencil" /> Update Legend</Title>
                <OptionsList>
                    {options
                        .sort((a, b) => b.liked_by.length - a.liked_by.length)
                        .map((option) => (
                            <Option key={option.option_id} optionData={option} />
                        ))
                    }
                </OptionsList>
                <Controls>
                    <textarea
                        rows={1}
                        value={userOption}
                        onChange={(e) => setUserOption(e.target.value)}
                        onKeyDown={handleTextareaKeyPress}
                        placeholder="Suggest a name"
                        autoComplete="off"
                    />
                    <button onClick={submitOption} disabled={waiting}>{waiting ? <i className="fa fa-spinner" /> : <i className="fa fa-paper-plane" />}</button>
                </Controls>
                <CloseButton onClick={onClose}><i className="fa fa-close" /></CloseButton>
            </LegendDialogue>
        </>
    );
};

export default UpdateLegend;

const LegendDialogue = styled.div`
    display: flex;
    flex-direction: column;
    width: auto;
    border-radius: 10px;
    background: #ececec;
    align-items: center;
    position: absolute;
    padding: 20px 10px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;

    @media only screen and (max-width: 600px) {
        width: 90%;
        padding: 20px 10px;
    }
`;

const Title = styled.h3`
    margin: 20px 0;
    font-size: 2em;
    color: #6559A2;
    padding: 0 20px;

    @media only screen and (max-width: 600px) {
        font-size: 22px;
    }
`;

const CloseButton = styled.button`
    font-size: 20px;
    position: absolute;
    top: 10px;
    right: 10px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: black;
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
`;

const OptionsList = styled.div`
    padding: 10px;
    margin: 0 8px;
    background: white;
    width: 100%;
    border-radius: 5px;
    min-height: 20vh;
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: hidden;
    box-shadow: rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px;

    @media only screen and (max-width: 600px) {
        max-height: 50vh;
    }
`;

const OptionContainer = styled.div`
    padding: 5px 10px;
    border-radius: 10px;
    box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
    margin-top: 10px;
    display: flex;
    justify-content: space-between;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        color: grey;
    }
`;

const Controls = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    margin-top: 15px;
    justify-content: space-around;

    textarea {
        padding: 10px;
        width: 75%;
        border: none;
        border-radius: 10px;
        box-shadow: rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px;
    }
    
    button {
        color: white;
        background: #6559A2;
        padding: 10px 15px;
        border-radius: 5px;
        border: none;
        box-shadow: rgba(0, 0, 0, 0.2) -3px 3px 0px;
    }
`;
